/**
 * CR125: Logo Asset Utilities
 * 
 * Shared helpers for identifying and filtering logo-suitable assets.
 */

import type { AppAsset } from "./types";

/**
 * Normalize a tag to lowercase trimmed string
 */
export function normalizeTag(tag: string): string {
  return tag.trim().toLowerCase();
}

/**
 * Normalize an array of tags
 */
export function normalizeTags(tags: string[]): string[] {
  return tags.map(normalizeTag).filter(Boolean);
}

/**
 * Check if an asset has a specific tag (case-insensitive)
 */
export function hasTag(asset: AppAsset, tag: string): boolean {
  const normalizedTag = normalizeTag(tag);
  return asset.tags?.some((t) => normalizeTag(t) === normalizedTag) ?? false;
}

/**
 * Check if an asset is suitable for use as a logo.
 * 
 * An asset is logo-suitable if:
 * - type is 'icon', OR
 * - tags include 'logo' (case-insensitive)
 */
export function isLogoAsset(asset: AppAsset): boolean {
  return asset.type === "icon" || hasTag(asset, "logo");
}

/**
 * Priority score for logo assets (higher = more preferred)
 * - Assets tagged 'logo' get priority 2
 * - Icon-type assets get priority 1
 * - Others get priority 0
 */
export function getLogoPriority(asset: AppAsset): number {
  if (hasTag(asset, "logo")) return 2;
  if (asset.type === "icon") return 1;
  return 0;
}

/**
 * Sort logo assets by priority (logo-tagged first, then icons)
 */
export function sortLogoAssets(assets: AppAsset[]): AppAsset[] {
  return [...assets].sort((a, b) => getLogoPriority(b) - getLogoPriority(a));
}

/**
 * Filter and sort assets suitable for logo selection.
 * Returns assets that are logo-suitable, sorted by priority.
 */
export function getLogoAssets(assets: AppAsset[]): AppAsset[] {
  const logoAssets = assets.filter(isLogoAsset);
  return sortLogoAssets(logoAssets);
}

/**
 * Check if an asset is a non-icon type (show warning if selected as logo)
 */
export function isNonIconLogoAsset(asset: AppAsset): boolean {
  return asset.type !== "icon" && hasTag(asset, "logo");
}

/**
 * Add 'logo' tag to an asset's tags list if not present
 */
export function addLogoTag(tags: string[]): string[] {
  const normalized = normalizeTags(tags);
  if (!normalized.includes("logo")) {
    normalized.push("logo");
  }
  return normalized;
}

/**
 * Default values for creating a new logo asset
 */
export const NEW_LOGO_ASSET_DEFAULTS = {
  name: "Header Logo",
  type: "icon" as const,
  tags: ["logo"],
  tint_mode: "none" as const,
  is_tileable: false,
  default_tint: null,
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
  <circle cx="50" cy="50" r="40" fill="currentColor" opacity="0.2"/>
  <text x="50" y="58" text-anchor="middle" font-size="24" fill="currentColor">Logo</text>
</svg>`,
};
